import { useState } from "react";
import { captureElement } from "../screenshot/capture.js";
import { uploadScreenshot } from "../screenshot/api.js";
import type { ImprintContext } from "../screenshot/imprint.js";
import { getSDK } from "../discord/activity.js";

type State = "idle" | "working" | "error";

interface ShareButtonBase {
  /** App route slug, e.g. "dim" → POST /api/dim/screenshots. */
  app: string;
  /** Brand label drawn on the imprint, e.g. "GammaLens", "DIM". */
  brandLabel: string;
  /** Discord username (no leading @). */
  username: string;
  /** Right-hand context line, e.g. "SPY · 5m", "Landscape". */
  contextLine: string;
  /** Optional caption symbol sent as X-Symbol on upload. */
  symbol?: string;
}

/**
 * A caller supplies exactly one capture strategy: either a CSS `target` for the
 * generic `captureElement`, or a full `capture` override (e.g. GammaLens's
 * scroll-aware one). The discriminated union makes "neither" a compile error.
 */
export type ShareButtonProps =
  | (ShareButtonBase & { target: string; capture?: never })
  | (ShareButtonBase & { capture: (ctx: ImprintContext) => Promise<Blob>; target?: never });

/**
 * Shared "Share to Discord" button: capture → upload to the staging webhook →
 * open Discord's share dialog. Used by every Terminal app. Capture is either
 * the generic `captureElement(target)` default or a per-app `capture` override.
 */
export function ShareButton(props: ShareButtonProps): JSX.Element {
  const { app, brandLabel, username, contextLine, symbol, target, capture } = props;
  const [state, setState] = useState<State>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleClick(): Promise<void> {
    if (state === "working") return;
    setState("working");
    setErrorMsg(null);
    try {
      const ctx: ImprintContext = { brandLabel, username, contextLine, takenAt: new Date() };
      const blob = capture ? await capture(ctx) : await captureElement(target ?? "", ctx);
      const { mediaUrl } = await uploadScreenshot(blob, app, symbol);

      const sdk = getSDK();
      if (!sdk) throw new Error("Discord SDK not initialised");
      await sdk.commands.openShareMomentDialog({ mediaUrl });

      setState("idle");
    } catch (err) {
      console.error("[ShareButton] failed:", err);
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setState("error");
      setTimeout(() => {
        setState("idle");
        setErrorMsg(null);
      }, 4000);
    }
  }

  const title =
    state === "working"
      ? "Capturing screenshot…"
      : state === "error"
        ? `Screenshot failed: ${errorMsg ?? "unknown error"}`
        : "Share screenshot to Discord";

  return (
    <button
      type="button"
      className={`screenshot-btn screenshot-btn--${state}`}
      onClick={handleClick}
      disabled={state === "working"}
      title={title}
      aria-label="Take screenshot and share to Discord"
      data-screenshot-exclude="true"
    >
      <span className="screenshot-btn__icon" aria-hidden="true">
        {state === "working" ? (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" opacity="0.25" />
            <path d="M21 12a9 9 0 0 0-9-9" />
          </svg>
        ) : state === "error" ? (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12" y2="13" />
            <line x1="12" y1="16.5" x2="12" y2="16.5" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7h3l2-2.5h6L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
        )}
      </span>
      <span className="screenshot-btn__label">
        {state === "working" ? "Sharing…" : state === "error" ? "Retry" : "Share"}
      </span>
    </button>
  );
}
